import React, { useState } from "react";
import { Calculator, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { countriesList, medicineTypes, serviceModes, contact } from "../mock";

export default function CourierCharges() {
  const [mode, setMode] = useState("pickup");
  const [country, setCountry] = useState("");
  const [medType, setMedType] = useState(medicineTypes[0]);
  const [mobile, setMobile] = useState("");
  const [result, setResult] = useState(null);

  const calculate = (e) => {
    e.preventDefault();
    if (!country) { toast.error("Please select a destination country"); return; }
    if (!mobile || mobile.length < 8) { toast.error("Please enter a valid mobile number"); return; }
    setResult({ country, price: 4400, weight: "0.5 kg", days: "5-6" });
    toast.success("Estimated charges calculated below!");
  };

  return (
    <>
      <Seo
        title="Courier Charges Calculator | Medicine Courier Rates from India | ShipMeds"
        description="Instantly estimate medicine courier charges from India to any country with ShipMeds. Transparent rates, free doorstep pickup and guaranteed international delivery."
        path="/courier-charges"
      />
      <PageHero title="Courier Charges Calculator" subtitle="Get an instant estimate for shipping your medicines internationally." crumb="Courier Charges" />
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl overflow-hidden card-shadow border border-slate-100">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 px-6 flex items-center gap-3">
              <Calculator className="w-6 h-6" />
              <h2 className="text-lg font-bold">Check Your Courier Charges</h2>
            </div>
            <form onSubmit={calculate} className="p-6 sm:p-8 space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Service Mode *</label>
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  {serviceModes.map((m) => (
                    <button type="button" key={m.id} onClick={() => setMode(m.id)} className={`py-3 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${mode === m.id ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-200 text-slate-600 hover:border-teal-300"}`}>{m.label}</button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Destination Country *</label>
                  <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                    <option value="">Select Country</option>
                    {countriesList.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Weight <span className="text-red-500">*</span></label>
                  <div className="w-full h-11 px-3 rounded-lg border border-teal-200 bg-teal-50 flex items-center justify-between">
                    <span className="text-teal-700 font-bold">0.5 kg</span>
                    <span className="text-[11px] font-medium text-teal-600 bg-white px-2 py-0.5 rounded-full">Standard</span>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Medicine Type *</label>
                  <select value={medType} onChange={(e) => setMedType(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                    {medicineTypes.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Mobile No. *</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-sm text-slate-600">+91</span>
                    <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Enter Mobile Number" className="w-full h-11 px-3 rounded-r-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  </div>
                </div>
              </div>
              <button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3.5 rounded-lg transition-colors">Calculate Charges</button>
            </form>
          </div>

          {result && (
            <div className="mt-8 bg-gradient-to-br from-teal-50 to-white border border-teal-100 rounded-2xl p-8 text-center fade-up">
              <p className="text-slate-500 mb-1">Estimated courier charge to</p>
              <h3 className="text-xl font-bold text-slate-900 mb-4">{result.country}</h3>
              <div className="text-5xl font-extrabold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">₹{result.price.toLocaleString()}</div>
              <p className="text-slate-500 mt-2">for {result.weight} · delivered in {result.days} working days</p>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-5 text-sm text-slate-600">
                {["Free doorstep pickup", "Documentation help", "Live tracking"].map((t) => (
                  <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-teal-500" />{t}</span>
                ))}
              </div>
              <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-7 py-3.5 rounded-lg transition-colors"><MessageCircle className="w-5 h-5" /> Book on WhatsApp</a>
              <p className="mt-3 text-xs text-slate-400">*Indicative estimate. Final rate confirmed by our team on booking.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
